import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Logger } from '@saviynt/common';
import {
  AlertBanner,
  type AlertBannerColorTheme,
  Box,
  Button,
  Icon,
  Loader,
  Typography,
} from '@saviynt/design-system';
import {
  type DataProvider,
  type FieldElement,
  type FormValues,
  MetadataForm,
  type MetadataFormRef,
} from '@saviynt/formforge';
import cx from 'classnames';

import {
  useLazyGetFormDataQuery,
  usePostCustomListMutation,
  usePostFormDataMutation,
} from '../../../features/ChatModule';
import {
  ALERT_BANNER_DEFAULT,
  CF_CRT_MED,
  CF_SEC_BLD,
  GLOBAL_ALERT_BANNER_TYPES,
  LIMIT_INLINE_COUNT,
  testIds,
} from '../../constants';
import { type ActionBlockOption, type EndpointBlock } from '../../types';
import {
  addTracker,
  convertNestedToLinear,
  countFields,
  FormAlertBanner,
  FormBody,
  type FormData,
  type FormFieldState,
  FormHeader,
  FormProvider,
  getFilledForm,
  processTracker,
  removeTracker,
  sanitizeFields,
  type TrackedFormData,
  validateForm,
} from '../Form';
import UiWrapper, { WRAPPER_MODE } from '../UiWrapper';

import './CombinedForm.css';

interface FormForgeData {
  ui: FieldElement[];
  dataProviders: Record<string, DataProvider>;
}

interface CombinedFormProps {
  id: string;
  title: string;
  isLast?: boolean;
  isFullView?: boolean;
  isViewing?: boolean;
  isRightWindowOpen?: boolean;
  isProcessing?: boolean;
  isChanged?: boolean;
  resetKey?: string;
  userId: string | number;
  customEndpoint: EndpointBlock;
  formforgeEndpoint: EndpointBlock;
  retentions?: {
    customFormData?: FormFieldState[];
    externalFormData?: FormValues;
  };
  onInlineExpand?: () => void;
  onExpand?: (
    customFormData: FormFieldState[],
    externalFormData: FormValues
  ) => void;
  onCollapse?: () => void;
  onSubmit?: (
    customFormData: FormFieldState[],
    externalFormData: FormValues
  ) => void;
}

/**
 * Renders a combined form that merges a custom (internal) form with an
 * external FormForge-driven metadata form. Supports inline, expanded,
 * full-view, and viewing modes with server-side validation.
 *
 * @param props - {@link CombinedFormProps}
 * @returns The combined form wrapped in a {@link FormProvider} and {@link UiWrapper}.
 */
function CombinedForm({
  id,
  title,
  isLast,
  isFullView,
  isViewing,
  isRightWindowOpen,
  isProcessing,
  isChanged,
  resetKey,
  retentions,
  userId,
  customEndpoint,
  formforgeEndpoint,
  onInlineExpand,
  onExpand,
  onCollapse,
  onSubmit,
}: CombinedFormProps) {
  const [currentMode, setCurrentMode] = useState(
    isFullView ? WRAPPER_MODE.EXPANDED : WRAPPER_MODE.INLINE
  );
  const [customFormData, setCustomFormData] = useState<TrackedFormData[]>([]);
  const [customFormAction, setCustomFormAction] = useState<ActionBlockOption[]>(
    []
  );
  const [alertBanner, setAlertBanner] = useState(ALERT_BANNER_DEFAULT);
  const [inlineItemCount, setInlineItemCount] = useState<number | null>(null);
  const [isExternalRetentionApplied, setIsExternalRetentionApplied] =
    useState(false);

  // Refs
  const hasExpandedRef = useRef(false);
  const [externalFormData, setExternalFormData] =
    useState<FormForgeData | null>(null);
  const [urlSlug, setUrlSlug] = useState<string>('');
  const [isExternalForm, setIsExternalForm] = useState(true);
  const externalFormRef = useRef<MetadataFormRef | null>(null);
  const formBodyRef = useRef<HTMLDivElement | null>(null);

  // API call
  const [
    fetchCustomFormData,
    { isError: isCustomError, isLoading: isCustomLoading },
  ] = useLazyGetFormDataQuery();
  const [
    fetchExternalFormData,
    { isError: isExternalError, isLoading: isExternalLoading },
  ] = usePostFormDataMutation();
  const [fetchValidations, { isLoading: isValidationLoading }] =
    usePostCustomListMutation();

  const handleCustomFetchFormData = useCallback(async () => {
    try {
      const controller = fetchCustomFormData({
        url: customEndpoint.url,
        data: { ...customEndpoint.queryParams, userId },
      });
      const response = await controller.unwrap();

      return response;
    } catch {
      throw Error('Error fetching form');
    }
  }, [fetchCustomFormData, customEndpoint, userId]);

  const handleValidations = useCallback(
    async ({
      url,
      data,
      queryParams,
    }: {
      url: string;
      data: object;
      queryParams?: object | undefined;
    }) => {
      try {
        const controller = fetchValidations({ url, data, queryParams });
        const response = await controller.unwrap();

        return response;
      } catch (error) {
        const typedError = error as {
          data?: {
            response?: { data?: { error: string; msg: string } };
            message: string;
          };
        };
        const errorMessage =
          typedError.data?.response?.data?.error ??
          typedError.data?.message ??
          'Something went wrong';

        throw Error(errorMessage);
      }
    },
    [fetchValidations]
  );

  const handleExternalFetchFormData = useCallback(async () => {
    try {
      const controller = fetchExternalFormData({
        url: `${formforgeEndpoint.url}?includeDefaultValues=true`,
        data: formforgeEndpoint.payload ?? {},
        isECMv6: formforgeEndpoint.isECMv6,
      });
      const response = await controller.unwrap();

      return response;
    } catch {
      throw Error('Error fetching form');
    }
  }, [fetchExternalFormData, formforgeEndpoint]);

  const triggerCustomFetchFormData = useCallback(() => {
    handleCustomFetchFormData()
      .then((response) => {
        const typedResponse = response as {
          elements: FormData[];
          actions: ActionBlockOption[];
        };
        const modifiedFormData = addTracker(typedResponse.elements);
        const fieldCount = countFields(modifiedFormData);

        // Apply retained values on top of the fetched form structure
        if (retentions?.customFormData) {
          setCustomFormData(
            getFilledForm(modifiedFormData, retentions.customFormData)
          );
        } else {
          setCustomFormData(modifiedFormData);
        }

        setCustomFormAction(typedResponse.actions);

        if (fieldCount > LIMIT_INLINE_COUNT) {
          setInlineItemCount(1);
        }
      })
      .catch((error: string) => {
        Logger.log('Error fetching custom form: ', error);
      });
  }, [handleCustomFetchFormData, retentions]);

  const triggerExternalFetchFormData = useCallback(() => {
    handleExternalFetchFormData()
      .then((response) => {
        const typedResponse = response as {
          data: {
            formData: FormForgeData & {
              formDebugInfo?: { urlSlug?: string };
            };
            urlSlug?: string;
          };
        };

        if (!('ui' in typedResponse.data.formData)) {
          setIsExternalForm(false);
        }

        setExternalFormData(typedResponse.data.formData);
        setUrlSlug(
          typedResponse.data.urlSlug ??
            typedResponse.data.formData.formDebugInfo?.urlSlug ??
            ''
        );
      })
      .catch((error: string) => {
        Logger.log('Error fetching external form: ', error);
      });
  }, [handleExternalFetchFormData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      triggerCustomFetchFormData();
      triggerExternalFetchFormData();
    }, 20);

    return () => clearTimeout(timer);
  }, [triggerExternalFetchFormData, triggerCustomFetchFormData]);

  // Form utils
  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (retentions && externalFormData && retentions.externalFormData) {
      timeout = setTimeout(() => {
        externalFormRef.current
          ?.setFormValues(retentions.externalFormData ?? {})
          .catch(() => {});
      }, 500);
    }

    setIsExternalRetentionApplied(true);

    return () => clearTimeout(timeout);
  }, [externalFormData, retentions, currentMode]);

  // Utils
  useEffect(() => {
    if (isFullView) {
      setCurrentMode(WRAPPER_MODE.EXPANDED);

      return;
    }

    if (isViewing) {
      setCurrentMode(WRAPPER_MODE.VIEWING);

      return;
    }

    if (isLast) {
      setCurrentMode(WRAPPER_MODE.INLINE);

      return;
    }

    setCurrentMode(WRAPPER_MODE.INLINE_COLLAPSED);
  }, [isFullView, isLast, isViewing, isChanged, resetKey]);

  useEffect(() => {
    if (
      !isExternalRetentionApplied ||
      inlineItemCount === null ||
      !onExpand ||
      hasExpandedRef.current
    ) {
      return;
    }

    setCurrentMode(WRAPPER_MODE.VIEWING);
    setInlineItemCount(null);
    hasExpandedRef.current = true;

    const untrackedForm = removeTracker(customFormData);
    const linearizedForm = convertNestedToLinear(untrackedForm);
    const sanitizedForm = sanitizeFields(linearizedForm);

    onExpand(sanitizedForm, retentions?.externalFormData ?? {});
  }, [
    isExternalRetentionApplied,
    inlineItemCount,
    onExpand,
    customFormData,
    retentions?.externalFormData,
  ]);

  const handleWrapperExpand = useCallback(() => {
    if (currentMode === WRAPPER_MODE.INLINE_COLLAPSED) {
      setCurrentMode(WRAPPER_MODE.INLINE);

      onInlineExpand?.();

      return;
    }

    if (currentMode === WRAPPER_MODE.INLINE && onExpand) {
      setCurrentMode(WRAPPER_MODE.VIEWING);

      const untrackedForm = removeTracker(customFormData);
      const linearizedForm = convertNestedToLinear(untrackedForm);
      const sanitizedForm = sanitizeFields(linearizedForm);
      const externalValues = externalFormRef.current?.getFormValues();

      onExpand(sanitizedForm, externalValues ?? {});

      return;
    }

    setCurrentMode(WRAPPER_MODE.INLINE_COLLAPSED);
  }, [currentMode, onExpand, onInlineExpand, customFormData]);

  const handleSubmit = useCallback(
    (externalValues: FormValues) => {
      if (isCustomError) {
        if (isFullView && currentMode === WRAPPER_MODE.EXPANDED && onCollapse) {
          onCollapse();
        }

        if (onSubmit) {
          onSubmit([], externalValues);
        }

        return;
      }

      const isVerified = validateForm(customFormData);

      if (!isVerified) {
        setCustomFormData((prev) => processTracker(prev));

        const parent = formBodyRef.current?.parentElement;

        parent?.scrollTo({
          top: 0,
          behavior: 'smooth',
        });

        setAlertBanner({
          ...ALERT_BANNER_DEFAULT,
          kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
          title: '',
          description: 'Mandatory fields are required',
          isOpen: true,
        });

        return;
      }

      const untrackedForm = removeTracker(customFormData);
      const linearizedForm = convertNestedToLinear(untrackedForm);
      const sanitizedForm = sanitizeFields(linearizedForm);

      // No validation exist for custom form
      const firstAction = customFormAction[0] as ActionBlockOption & {
        validationEndpoint?: EndpointBlock;
      };

      if (customFormAction.length === 0 || !firstAction.validationEndpoint) {
        if (isFullView && currentMode === WRAPPER_MODE.EXPANDED && onCollapse) {
          onCollapse();
        }

        if (onSubmit) {
          onSubmit(sanitizedForm, externalValues);
        }

        return;
      }

      const currentAction = customFormAction[0] as ActionBlockOption & {
        validationEndpoint: EndpointBlock;
      };

      const wantedFields = currentAction.validationEndpoint.payload as {
        fields: {
          validationType: string;
          validateValue: string;
        }[];
      };
      const fields = wantedFields.fields.map((field) => {
        const { validateValue } = field;
        const match = validateValue.match(/{{\s*([^}]+)\s*}}/);

        if (match) {
          const placeholder = match[1];
          const targetField = sanitizedForm.find(
            (sf) => sf.name === placeholder
          );

          return {
            ...field,
            validateValue: targetField?.value ?? '',
          };
        }

        return field;
      });
      const validatePayload = {
        url: currentAction.validationEndpoint.url,
        data: {
          ...currentAction.validationEndpoint.payload,
          fields,
        },
      };

      handleValidations(validatePayload)
        .then((response) => {
          const typedResponse = response as {
            valid: boolean;
            validations: {
              validationType: string;
              valid: boolean;
              errors: string[];
            }[];
          };

          if (!typedResponse.valid) {
            setAlertBanner({
              ...ALERT_BANNER_DEFAULT,
              kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
              title: '',
              description: typedResponse.validations
                .filter((v) => !v.valid)
                .map((v) => v.errors.join(', '))
                .join(', '),
              isOpen: true,
            });

            return;
          }

          if (
            isFullView &&
            currentMode === WRAPPER_MODE.EXPANDED &&
            onCollapse
          ) {
            onCollapse();
          }

          if (onSubmit) {
            onSubmit(sanitizedForm, externalValues);
          }
        })
        .catch((error) => {
          const typedError = error as { message: string };

          setAlertBanner({
            ...ALERT_BANNER_DEFAULT,
            kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
            title: '',
            description: typedError.message,
            isOpen: true,
          });

          Logger.log(typedError.message);
        });
    },
    [
      isCustomError,
      customFormData,
      customFormAction,
      handleValidations,
      isFullView,
      currentMode,
      onCollapse,
      onSubmit,
    ]
  );

  const renderBody = useCallback(() => {
    if (isExternalError && isCustomError) {
      return (
        <Box
          className='ListViewer-errorModal'
          dataTestId={testIds.COMBINED_FORM_ERROR}>
          <Icon kind='StopProcess' size='large' color={CF_CRT_MED} />
          <Typography kind='body2' color={CF_CRT_MED}>
            <FormattedMessage
              id='apz7lc'
              defaultMessage='System encountered an error. Please contact the Support Administrator'
              description='System encountered an error. Please contact the Support Administrator'
            />
          </Typography>
        </Box>
      );
    }

    if (isCustomLoading || isExternalLoading) {
      return (
        <Box
          tag='div'
          className='FormForge-loaderModal'
          dataTestId={testIds.COMBINED_FORM_LOADER}>
          <Loader kind='dots' size='small' format='inline' color='brand' />
        </Box>
      );
    }

    return (
      <>
        <FormAlertBanner />
        <AlertBanner
          colorTheme={alertBanner.kind as AlertBannerColorTheme}
          title={alertBanner.title}
          description={alertBanner.description}
          shouldAutoDismiss
          isColonVisible={false}
          isVisible={alertBanner.isOpen}
          dataTestId='savi-copilot-combined-form-alert-banner'
          className='FormAlertBanner'
          onCancel={() => {
            setAlertBanner({
              ...ALERT_BANNER_DEFAULT,
              kind: alertBanner.kind,
              isOpen: false,
            });
          }}
        />
        {isFullView && (
          <Box tag='div'>
            <Typography
              kind='h2'
              color={CF_SEC_BLD}
              dataTestId={testIds.COMBINED_FORM_TITLE}>
              {title}
            </Typography>
          </Box>
        )}
        {!isCustomError && (
          <Box
            tag='div'
            className={cx('Form-section', 'Form-section--isCombinedFullView')}>
            <FormBody
              formData={customFormData}
              setFormData={setCustomFormData}
            />
          </Box>
        )}
        {!isExternalError && externalFormData !== null && (
          <Box
            tag='div'
            className={cx('Form-section', 'Form-section--isCombinedFullView')}>
            {!isCustomError && (
              <FormHeader
                title='Dynamic Attributes'
                level={-1}
                index={-1}
                isSubsection={false}
              />
            )}
            <Box
              tag='div'
              className={cx(
                'Form-section',
                isFullView && 'Form-section--isCombinedFullView'
              )}>
              {isExternalForm ? (
                <MetadataForm
                  ref={externalFormRef}
                  canvasFields={externalFormData.ui}
                  dataProviders={externalFormData.dataProviders}
                  onSubmit={handleSubmit}
                  onValuesChange={() => {}}
                  logoAlignment='center'
                  dataTheme='light'
                  dataTestId='basic-form-preview'
                  classNames={{
                    root: 'CombinedForm-FormForge',
                    row: 'CombinedForm-FormForge-row',
                  }}
                  isDynamicAttributeForm
                  isPreview={false}
                  urlSlug={urlSlug}
                />
              ) : (
                <Box tag='div' className='CombinedForm-emptyDynamicform'>
                  <Typography kind='body2' color={CF_SEC_BLD}>
                    <FormattedMessage
                      id='SAGZZx'
                      defaultMessage='No dynamic attributes are configured'
                      description='No dynamic attributes are configured'
                    />
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </>
    );
  }, [
    isExternalError,
    isCustomError,
    isCustomLoading,
    isExternalLoading,
    isExternalForm,
    externalFormData,
    alertBanner.kind,
    alertBanner.title,
    alertBanner.description,
    alertBanner.isOpen,
    isFullView,
    title,
    customFormData,
    handleSubmit,
    urlSlug,
  ]);

  return (
    <FormProvider>
      <UiWrapper
        id={id}
        isActive={Boolean(isLast)}
        isEdited={isChanged}
        isRightWindowOpen={Boolean(isRightWindowOpen)}
        mode={currentMode}
        title={title}
        isDisabled={
          (isExternalError && isCustomError) ||
          isCustomLoading ||
          isExternalLoading ||
          isProcessing
        }
        onExpand={handleWrapperExpand}
        onCollapse={onCollapse}
        onClose={() => {
          setCurrentMode(WRAPPER_MODE.INLINE_COLLAPSED);
        }}
        footer={
          <Box tag='div' className='CombinedForm-footerWrapper'>
            <Button
              kind='outlined'
              onClick={() => {
                if (isExternalError && isCustomError) {
                  if (
                    isFullView &&
                    currentMode === WRAPPER_MODE.EXPANDED &&
                    onCollapse
                  ) {
                    onCollapse();
                  }

                  if (onSubmit) {
                    onSubmit([], {});
                  }

                  return;
                }

                if (isExternalError || !isExternalForm) {
                  handleSubmit({});

                  return;
                }

                externalFormRef.current?.validateAndSubmit().catch(() => {
                  const parent = formBodyRef.current?.parentElement;

                  parent?.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                  });

                  setAlertBanner({
                    ...ALERT_BANNER_DEFAULT,
                    kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
                    title: '',
                    description: 'Mandatory fields are required',
                    isOpen: true,
                  });
                });
              }}
              className='ListViewer-footerActionButton'
              dataTestId={testIds.COMBINED_FORM_CONTINUE_BTN}
              isDisabled={
                isValidationLoading ||
                (isExternalError && isCustomError) ||
                isCustomLoading ||
                isExternalLoading ||
                isProcessing
              }
              isLoading={isValidationLoading}
              loaderText='Validating...'>
              <FormattedMessage
                id='8jETK0'
                defaultMessage='Continue'
                description='Continue'
              />
            </Button>
          </Box>
        }
        className='CombinedForm'>
        <div ref={formBodyRef} style={{ width: '100%' }}>
          {renderBody()}
        </div>
      </UiWrapper>
    </FormProvider>
  );
}

CombinedForm.defaultProps = {
  isLast: true,
  isFullView: false,
  isProcessing: false,
  isViewing: false,
  isRightWindowOpen: false,
  isChanged: false,
  resetKey: '',
  retentions: null,
  onInlineExpand: undefined,
  onExpand: undefined,
  onCollapse: undefined,
  onSubmit: undefined,
};

export default memo(CombinedForm);
